import {formatDateRange} from 'bigdatr-style';
import {isSame, now, startOf, subtract} from 'stringdate';
import {CreativeDateRangeFilter} from '~/report/data/ReportItemDefinitionTypes';

// @intent - display relative or absolute dates nicely
export default function formatDateRangeFilter(input: {
    dateRange: CreativeDateRangeFilter;
    /** Override endDate, defaults to now(). Useful for prelim data labels */
    lastKnownDate?: string;
    overrideAbsoluteEndDateForCurrentMonth?: boolean;
    /** Opt in to not display the days of the months, but only the months and years. Will display a date as May 2024, instead of May 1 - May 31 2024*/
    hideDays?: boolean;
}) {
    const dates = dateRangeFilterToAbsoluteDates({
        dateRange: input.dateRange,
        lastKnownDate: input.lastKnownDate,
        overrideAbsoluteEndDateForCurrentMonth: input.overrideAbsoluteEndDateForCurrentMonth
    });
    if (dates) return formatDateRange(dates.startDate, dates.endDate, {hideDays: input.hideDays});
}

export function dateRangeFilterToAbsoluteDates(input: {
    dateRange: CreativeDateRangeFilter;
    /** Override endDate, defaults to now(). Useful for prelim data labels */
    lastKnownDate?: string;
    /** Creative date picker is now day based, so we can use any selected day for the present month.
     * But for media value, we auto select the present day if the selected end month is the present month */
    overrideAbsoluteEndDateForCurrentMonth?: boolean;
}) {
    const {dateRange, lastKnownDate, overrideAbsoluteEndDateForCurrentMonth = true} = input;
    const today = now();
    if (dateRange.absolute) {
        let endDate = dateRange.absolute.endDate;
        if (
            overrideAbsoluteEndDateForCurrentMonth &&
            isSame('month', dateRange.absolute.endDate)(now())
        ) {
            endDate = today;
        }
        return {startDate: dateRange.absolute.startDate, endDate};
    } else if (dateRange.relative) {
        // If the prelim month is the current month, we use `now()` as the end date.
        // We do this because the prelim month will always be the first of the month,
        // but in the UI we display the prelim month as if its until the current day.
        const endDate = lastKnownDate
            ? isSame('month', lastKnownDate)(now())
                ? today
                : lastKnownDate
            : today;
        // Subtracting 1 because the range is inclusive
        const subtractingMonths = dateRange.relative.months - 1;
        const startDate = startOf('month')(subtract(`P${subtractingMonths}M`)(endDate));
        return {
            startDate,
            endDate
        };
    }
}
